import clsx from 'clsx';
import React from 'react';
import {IconName} from '@lightricks/react-design-system';
import {getApprovalBreakdown} from '@/utils/approvalStatusUtils';
import {
  ContentSubmission,
  ContentSubmissionState,
} from '@/types/contentSubmission';
import {DropdownOption} from '@/components/dropdown';
import styles from './ContentSubmissionCard.module.scss';
import {CardThumbnailArea, CardInfoArea} from './SubComponents';
import {ContentSubmissionAction} from './contentSubmissionCardTypes';
import getContentTypeLabel from './getContentTypeLabel';
import {ActionMenuItem} from './useContentSubmissionActionMenuItems';

export type ContentSubmissionCardProps = {
  testID?: string;
  contentSubmission: ContentSubmission;
  isSelected?: boolean;
  onSelect?: (contentSubmissionId: string, selected: boolean) => void;
  onAction: (
    action: ContentSubmissionAction,
    contentSubmissionId: string,
    contentSubmissionVersionId?: string
  ) => void;
  onClick?: (
    event: React.MouseEvent<HTMLElement>,
    contentSubmissionId: string
  ) => void;
  /**
   * CSS aspect-ratio value applied to the thumbnail (e.g. "4/3", "1/1", "16/9").
   * Defaults to undefined — thumbnail renders at its natural height, which is
   * required for masonry layouts. Pass a value when a fixed height is needed.
   */
  aspectRatio?: string;
  isOverdue?: boolean;
  showAiScore?: boolean;
  campaignNetwork?: string | null;
  /**
   * Action menu items to display in the 3-dot menu.
   * When omitted, no menu is rendered.
   */
  actionMenuItems?: ActionMenuItem[];
  onActionsMenuOpen?: (context: {creator_id?: string; flow_id: string}) => void;
  onActionsMenuClose?: () => void;
  profileLink?: string;
  onActionSelected?: (
    action: ContentSubmissionAction,
    context: {creator_id?: string; flow_id: string}
  ) => void;
};

// ─── Main component ───────────────────────────────────────────────────────────

function ContentSubmissionCard(props: ContentSubmissionCardProps) {
  const {
    testID = 'content-submission-card',
    contentSubmission,
    isSelected = false,
    onSelect,
    onAction,
    onClick,
    aspectRatio,
    isOverdue,
    campaignNetwork,
    showAiScore = false,
    actionMenuItems,
    onActionsMenuOpen,
    onActionsMenuClose,
    profileLink,
    onActionSelected,
  } = props;

  const {
    id: contentSubmissionId,
    kind,
    thumbnailUrls,
    url,
    aasmState,
    approvalStatus,
    latestVersionCommentCount,
    profile,
    deliverableKind,
    versionCount,
    latestVersionCampaignFitScore,
  } = contentSubmission;

  const {pendingDepartments, approvedStepsNames} =
    getApprovalBreakdown(approvalStatus);

  const [thumbnailUrl] = thumbnailUrls ?? [];

  const isStory =
    kind === 'mixed_media' && !!campaignNetwork?.includes('instagram');

  const handleCheckboxChange = onSelect
    ? (checked: boolean) => {
        onSelect(contentSubmissionId, checked);
      }
    : undefined;

  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event, contentSubmissionId);
  };

  const cardContext = {
    creator_id: profile?.id,
    flow_id: contentSubmissionId,
  };

  const handleActionChange = (option: DropdownOption) => {
    const item = actionMenuItems?.find((i) => i.id === option.id);
    if (item) {
      onActionSelected?.(item.action, cardContext);
      onAction(
        item.action,
        contentSubmissionId,
        contentSubmission.latestVersionId || ''
      );
    }
  };

  let contentTypeIcon: IconName | undefined;
  if (isStory) {
    contentTypeIcon = 'Social-InstagramStories';
  } else if (kind === 'video') {
    contentTypeIcon = 'Social-InstagramReels';
  }
  const creatorName = profile?.displayName ?? '';
  const contentTypeLabel = getContentTypeLabel(
    deliverableKind,
    kind,
    campaignNetwork
  );
  const versionLabel =
    versionCount && versionCount > 1 ? ` V${versionCount}` : '';

  const hasAiScore = latestVersionCampaignFitScore != null;

  return (
    <div
      data-testid={testID}
      data-id={contentSubmission.id}
      className={clsx(styles.card, isSelected && styles.selected)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
    >
      <CardThumbnailArea
        testID={testID}
        thumbnailUrl={thumbnailUrl}
        videoUrl={kind === 'video' ? url : undefined}
        creatorName={creatorName}
        aspectRatio={aspectRatio}
        contentTypeIcon={contentTypeIcon}
        latestVersionCommentCount={latestVersionCommentCount}
        childContentSubmissionCount={
          contentSubmission.childContentSubmissionCount
        }
        isStory={isStory}
        isSelected={isSelected}
        onCheckboxChange={handleCheckboxChange}
        actionMenuOptions={actionMenuItems}
        onActionChange={handleActionChange}
        onActionsMenuOpen={
          onActionsMenuOpen ? () => onActionsMenuOpen(cardContext) : undefined
        }
        onActionsMenuClose={onActionsMenuClose}
      />
      <CardInfoArea
        aasmState={aasmState as ContentSubmissionState}
        pendingDepartments={pendingDepartments}
        approvedCount={approvedStepsNames.length}
        versionCount={versionCount}
        isOverdue={isOverdue}
        dueAt={contentSubmission.dueAt}
        aiScore={latestVersionCampaignFitScore ?? null}
        footerLabelProps={{
          creatorName,
          profileLink,
          contentTypeLabel,
          versionLabel,
        }}
        showAiScore={hasAiScore && showAiScore}
      />
    </div>
  );
}

export default ContentSubmissionCard;
